/**
 * EmailVerificationDialog — the "resend verification code + enter code" flow
 * used from Settings. Same UX + endpoints as the onboarding EmailVerifyBanner,
 * just packaged as a dialog you can open from anywhere.
 *
 * Feb 2026: added because the resend UI previously only lived inside the
 * onboarding banner, which meant users who dismissed it (or never visited
 * /profile/setup after signup) had no way to trigger a resend. 138 preview
 * email-provider users were stranded before this landed.
 */
import { useEffect, useState } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Mail, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function EmailVerificationDialog({ open, onClose }) {
  const { user, checkAuth } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [status, setStatus] = useState(null); // 'sent' | 'resend_failed' | 'no_api_key' | null

  useEffect(() => {
    if (!open) return undefined;
    // Prime delivery state so the UI shows "we sent it" vs "couldn't send".
    let alive = true;
    api.get('/auth/verification-status')
      .then((r) => alive && setStatus(r.data?.last_send_status || null))
      .catch(() => {});
    return () => { alive = false; };
  }, [open]);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const id = setInterval(() => setResendCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCountdown]);

  const verify = async () => {
    if (code.trim().length !== 6) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/verify-email', { code: code.trim() });
      toast.success('Email verified ✓');
      await checkAuth?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (resendCountdown > 0 || busy) return;
    setBusy(true);
    try {
      await api.post('/auth/resend-verification');
      toast.success('New code sent — check your inbox');
      setStatus('sent');
      setResendCountdown(60);
    } catch (e) {
      setStatus('resend_failed');
      toast.error(e.response?.data?.detail || 'Email service unavailable — try again in a moment');
    } finally {
      setBusy(false);
    }
  };

  const failed = status === 'resend_failed' || status === 'no_api_key';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-white border-[var(--hairline)] rounded-2xl p-6 max-w-sm" data-testid="email-verify-dialog">
        <DialogTitle className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-[#0F0F10] mb-1">
          {failed ? <AlertTriangle size={16} className="text-[#B45309]" /> : <Mail size={16} className="text-[#B91C1C]" />}
          Verify your email
        </DialogTitle>
        <DialogDescription className="sr-only">
          Enter the 6-digit verification code we emailed you, or tap Resend to get a new one.
        </DialogDescription>
        <p className="text-[12px] text-[#4B4B52] mb-4">
          {failed
            ? `We couldn't send the last code to ${user?.email}. Tap Resend to try again.`
            : (<>Enter the 6-digit code we sent to <span className="font-semibold text-[#0F0F10]">{user?.email}</span>.</>)}
        </p>
        <input
          type="text" inputMode="numeric" pattern="[0-9]*"
          maxLength={6} placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="w-full text-center font-mono text-2xl tracking-[0.5em] font-bold py-3 rounded-xl bg-[var(--surface-2)] border border-[var(--hairline-strong)] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30"
          data-testid="settings-verify-code-input"
          autoFocus
        />
        <div className="flex gap-2 mt-3">
          <button
            onClick={verify}
            disabled={busy || code.length !== 6}
            data-testid="settings-verify-submit"
            className="flex-1 px-4 py-2.5 rounded-xl brand-gradient text-white text-[13px] font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2 transition"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            Verify
          </button>
          <button
            onClick={resend}
            disabled={busy || resendCountdown > 0}
            data-testid="settings-verify-resend"
            className="px-3 py-2.5 rounded-xl bg-white border border-[var(--hairline-strong)] text-[#4B4B52] text-[12px] font-semibold hover:bg-black/[0.02] disabled:opacity-50 transition"
          >
            {resendCountdown > 0 ? `Resend ${resendCountdown}s` : 'Resend'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
